// sagas/sentenceSaga.js
import { takeLatest, call, put, select } from "redux-saga/effects";
import {
  fetchSentencesRequest,
  fetchSentencesSuccess,
  fetchSentencesFailure,
  setSentencesLoadingIds,
  fetchSentencesSuccessNoFetch,
} from "../slices/sentenceSlice";
import { fetchSentencesFromApi } from "../api/sentenceApi";
import {
  selectCurrentPageFetchedBookIds,
  selectIsSentenceFetched,
  selectSelectedBookIds,
} from "../selectors/sentenceSelectors";
import {
  setCurrentPage,
  setItemsPerPage,
  setTotalPages,
} from "../slices/filterSlice";
import { setFetchedBookIds } from "../slices/bookSlice";

function* fetchSentencesSaga(action) {
  try {
    // Destructure the action to get parameters for API call
    const { books, searchQuery, itemsPerPage, currentPage, chapters } =
      action.payload;
    let currentPageNeededBooksIds;
    if (searchQuery.length) {
      // ✅ Check if sentences are already fetched
      const selectedBookIds = yield select(selectSelectedBookIds);
      currentPageNeededBooksIds = selectedBookIds.map((item) => item.id);
    } else {
      currentPageNeededBooksIds = yield select(selectCurrentPageFetchedBookIds); // what has already been fetched for this page
    }
    // find needed Book Ids per page
    const neededBookIds = currentPageNeededBooksIds;
    if (neededBookIds.length) {
      // if books are needed
      // Set loading ids (books that are being fetched)
      yield put(setSentencesLoadingIds(neededBookIds));
      const currentChapter =
        chapters.filter((chap) => chap.book_id === books[0].id)[0]
          ?.chapter_id || 1;
      // check here
      // ✅ Check if sentences are already fetched
      const { isFetched, excludeIds } = yield select(
        selectIsSentenceFetched,
        currentChapter,
        currentPage,
        itemsPerPage
      );

      const options = {
        book_id: neededBookIds.join(","),
        search: searchQuery,
        limit: itemsPerPage,
        page: currentPage,
        exclude_sentences: excludeIds.join(","),
      };
      if (searchQuery.length === 0) {
        options.chapter_id = currentChapter;
      }

      if (isFetched) {
        console.log("Skipping API call: Sentences already exist.");
        return; // 🚀 Skip API call if already fetched
      }

      // Call the API to fetch sentences
      const sentencesCondensed = yield call(fetchSentencesFromApi, options);

      const fetchedBookIds = new Set(
        sentencesCondensed.sentences.map((sentence) => sentence.book_id)
      );
      let sentences = sentencesCondensed.sentences.map((item) => ({
        ...item,
        page: sentencesCondensed.page,
      }));
      sentencesCondensed.sentences = sentences;

      // Dispatching setTotalPages action
      yield put(setFetchedBookIds(Array.from(fetchedBookIds)));
      yield put(setTotalPages(sentencesCondensed.totalPages));
      yield put(setCurrentPage(sentencesCondensed.page));
      yield put(setItemsPerPage(sentencesCondensed.limit));
      // Update the state with the sentences data
      yield put(fetchSentencesSuccess(sentencesCondensed));
    } else {
      yield put(fetchSentencesSuccessNoFetch());
    }
  } catch (error) {
    // If there's an error, dispatch the failure action
    yield put(fetchSentencesFailure(error.message));
  }
}

// Watcher saga: listens for the fetchSentencesRequest action and triggers the worker saga
export function* watchSentenceSaga() {
  yield takeLatest(fetchSentencesRequest.type, fetchSentencesSaga);
}
